import { recordRecentTask } from "@/lib/recent-opened";
import { markWallItemOpened } from "@/lib/wall-new-opened";
import { useTasksStore } from "@/stores/tasks-store";
import { useTaskPocketStore } from "@/stores/task-pocket-store";
import { usePresentationStore } from "@/stores/presentation-store";
import { useProjectStore } from "@/stores/project-store";
import { captureDesktopChatAuthScopeGuard } from "@/desktop/desktop-chat-auth-scope";
import { isFrameInFocus } from "@/lib/task-focus-tile";
import { workspaceViewAdapter } from "@/lib/workspace-view-adapter";
import { boardFrameOnScreen, MIN_ANCHORED_ZOOM } from "./task-pocket-placement";
let latestTaskOpen = 0;

/**
 * The board's list holds the newest tasks only, so a task opened by id (an
 * older done one, from search or a notification) is read from the current
 * project into the store first. A task it does not hold stays closed.
 */
async function loadTaskOutsideBoardList(taskId: string): Promise<void> {
  const projectId = useProjectStore.getState().currentProject?.id;
  if (!projectId) return;
  try {
    const { getTask } = await import("@/api/tasks");
    useTasksStore
      .getState()
      .handleTaskCreated(await getTask(projectId, taskId));
  } catch {
    // Not this project's task, or gone: nothing opens.
  }
}

/** Start before asynchronous project resolution; pass this guard through the eventual open. */
export function captureTaskOpenIntent(): () => boolean {
  const request = ++latestTaskOpen;
  const authCurrent = captureDesktopChatAuthScopeGuard();
  const requestId = useTaskPocketStore.getState().beginOpen();
  return () =>
    request === latestTaskOpen &&
    authCurrent() &&
    requestId === useTaskPocketStore.getState().requestId;
}

export async function openTaskDetails(
  taskId: string,
  options: { boardFrameId?: string; shouldContinue?: () => boolean } = {},
): Promise<void> {
  const stillCurrent = options.shouldContinue ?? captureTaskOpenIntent();
  if (!stillCurrent()) return;
  if (!useTasksStore.getState().tasks.some((item) => item.id === taskId)) {
    await loadTaskOutsideBoardList(taskId);
    if (!stillCurrent()) return;
  }
  const pocketBeforeOpen = useTaskPocketStore.getState();
  const record = () => {
    markWallItemOpened(`task:${taskId}`);
    const task = useTasksStore
      .getState()
      .tasks.find((item) => item.id === taskId);
    if (task) recordRecentTask(task);
  };
  const board = options.boardFrameId;
  if (
    board &&
    boardFrameOnScreen(board) &&
    (isFrameInFocus(board, usePresentationStore.getState().presentation) ||
      workspaceViewAdapter().transform().zoom >= MIN_ANCHORED_ZOOM)
  ) {
    useTaskPocketStore
      .getState()
      .open(taskId, { kind: "board", frameId: board });
    record();
    return;
  }
  const projectId = useProjectStore.getState().currentProject?.id;
  const { isTauri } = await import("@/lib/is-tauri");
  if (isTauri()) {
    const { openWorkspaceOnActiveDisplay } =
      await import("@/desktop/onboarding/controller-utils");
    await openWorkspaceOnActiveDisplay();
  }
  if (
    pocketBeforeOpen === useTaskPocketStore.getState() &&
    stillCurrent() &&
    projectId === useProjectStore.getState().currentProject?.id
  ) {
    useTaskPocketStore.getState().open(taskId, { kind: "docked" });
    record();
  }
}
